import React from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import remarkGfm from 'remark-gfm'
import { CaseStudySection as CaseStudySectionType } from '../../types/caseStudy'
import { Card, Heading, Text } from '../ui'
import { TechnicalDiagram } from './TechnicalDiagram'

/**
 * Props interface for the CaseStudySection component
 */
export interface CaseStudySectionProps {
  /** The case study section data to display */
  section: CaseStudySectionType
  /** Additional CSS classes */
  className?: string
}

/**
 * Case Study Section component for displaying structured case study content
 *
 * Features:
 * - Rich markdown content with proper typography
 * - Code block syntax highlighting
 * - Technical diagram integration
 * - Metrics display with visual emphasis
 * - Responsive design with professional styling
 * - Accessible content structure
 */
export const CaseStudySection: React.FC<CaseStudySectionProps> = ({
  section,
  className = '',
}) => {
  const { id, title, content, codeBlock, diagram, metrics } = section

  // Preprocess content to convert to proper markdown
  const processedContent = content
    // Convert bullet points to markdown lists
    .replace(/^• /gm, '- ')
    // Fix spacing around section headers
    .replace(/\*\*([^*]+):\*\*/g, '\n\n**$1:**\n')
    // Fix spacing around numbered sections
    .replace(/\*\*(\d+\.\s[^*]+)\*\*/g, '\n\n**$1**\n')
    // Ensure proper line breaks after bullets
    .replace(/^- \*\*([^*]+)\*\*:/gm, '- **$1**:')
    // Clean up multiple newlines
    .replace(/\n{3,}/g, '\n\n')
    // Trim whitespace
    .trim()

  return (
    <section id={id} className={`case-study-section ${className}`}>
      <Card variant="surface" padding="lg" className="section-card">
        <Heading as="h2" size="2xl" className="section-title">
          {title}
        </Heading>

        {/* Main Content - Rendered as Markdown */}
        <div className="section-content markdown-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            components={{
              // Custom component overrides for consistent styling
              p: ({ children }) => (
                <Text
                  size="base"
                  variant="primary"
                  className="content-paragraph"
                >
                  {children}
                </Text>
              ),
              strong: ({ children }) => (
                <strong className="content-strong">{children}</strong>
              ),
              ul: ({ children }) => (
                <ul className="content-list">{children}</ul>
              ),
              li: ({ children }) => (
                <li className="content-list-item">
                  <Text size="base" variant="primary">
                    {children}
                  </Text>
                </li>
              ),
              h3: ({ children }) => (
                <Heading as="h3" size="lg" className="content-subheading">
                  {children}
                </Heading>
              ),
              h4: ({ children }) => (
                <Heading as="h4" size="lg" className="content-subheading">
                  {children}
                </Heading>
              ),
              code: ({ children, className, ...props }: any) => {
                const isInline = !className?.includes('language-')
                if (isInline) {
                  return (
                    <code className="inline-code" {...props}>
                      {children}
                    </code>
                  )
                }
                return (
                  <code className={className} {...props}>
                    {children}
                  </code>
                )
              },
              pre: ({ children }) => (
                <Card variant="default" padding="md" className="code-container">
                  <pre className="code-block">{children}</pre>
                </Card>
              ),
            }}
          >
            {processedContent}
          </ReactMarkdown>
        </div>

        {/* Code Block */}
        {codeBlock && (
          <div className="section-code-block">
            {codeBlock.title && (
              <Text size="sm" variant="secondary" className="code-title">
                {codeBlock.title}
              </Text>
            )}
            <Card variant="default" padding="md" className="code-container">
              <pre className={`language-${codeBlock.language}`}>
                <code>{codeBlock.code}</code>
              </pre>
            </Card>
          </div>
        )}

        {/* Technical Diagram */}
        {diagram && diagram.imageUrl && (
          <div className="section-diagram">
            <TechnicalDiagram
              diagram={{
                type: diagram.type,
                description: diagram.description,
                imageUrl: diagram.imageUrl,
              }}
            />
          </div>
        )}

        {/* Section Metrics */}
        {metrics && metrics.length > 0 && (
          <div className="section-metrics">
            <Text size="sm" variant="secondary" className="metrics-title">
              Key Metrics
            </Text>
            <div className="metrics-grid">
              {metrics.map((metric, index) => (
                <Card
                  key={index}
                  variant="glass"
                  padding="sm"
                  className="metric-item"
                >
                  <div className="metric-value">{metric.value}</div>
                  <Text size="xs" variant="secondary" className="metric-label">
                    {metric.label}
                  </Text>
                  {metric.description && (
                    <Text
                      size="xs"
                      variant="tertiary"
                      className="metric-description"
                    >
                      {metric.description}
                    </Text>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}
      </Card>
    </section>
  )
}

export default CaseStudySection
